import { useEffect, useRef } from 'react';
import { useChatStore } from '@/stores/chatStore';
import { MessageBubble } from './MessageBubble';
import { TemplateGrid } from './TemplateGrid';
import { LoadingPulse } from './LoadingPulse';
import type { Template } from './Templates';

// How close (px) to the bottom the user must be for streaming deltas to
// keep auto-scrolling. Beyond this, they've scrolled up to re-read an
// earlier answer — yanking the viewport back down per token is hostile.
const AUTOSCROLL_THRESHOLD_PX = 120;

interface Props {
  onClarifyPick?: (messageId: string, interpretation: string) => void;
  onTemplatePick?: (template: Template) => void;
  onSuggestionPick?: (text: string) => void;
}

export function MessageList({ onClarifyPick, onTemplatePick, onSuggestionPick }: Props = {}) {
  // Narrow selectors — a whole-store subscription re-rendered this list
  // (and every bubble in it) for unrelated state like thinkingText on
  // every SSE token. Actions/ids selected individually are stable.
  const messages = useChatStore((s) => s.messages);
  const loadingPhase = useChatStore((s) => s.loadingPhase);
  const conversationId = useChatStore((s) => s.conversationId);
  const containerRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Auto-scroll only when the user is already pinned near the bottom.
    const el = containerRef.current;
    if (el) {
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distanceFromBottom > AUTOSCROLL_THRESHOLD_PX) return;
    }
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loadingPhase]);

  if (messages.length === 0) {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="px-6 pt-6 text-center text-sm text-text-secondary space-y-1">
          <p className="font-medium text-text-primary dark:text-text-primary-dark">
            Ask anything about your spreadsheet
          </p>
          <p className="text-xs">
            Try a template below, or type a question — Hinglish, Hindi, or English all work.
          </p>
        </div>
        {onTemplatePick && <TemplateGrid onPick={onTemplatePick} />}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="flex-1 overflow-y-auto p-3 space-y-3">
      {messages.map((m, i) => (
        // Fallback index is defensive only — chatStore now guarantees
        // a non-empty id after finalizeAssistant. Keeps us resilient
        // against any future regressions that zero an id out.
        <MessageBubble
          key={m.id || `idx-${i}-${m.timestamp}`}
          m={m}
          conversationId={conversationId}
          isLast={i === messages.length - 1}
          onClarifyPick={onClarifyPick}
          onSuggestionPick={onSuggestionPick}
        />
      ))}
      {loadingPhase && <LoadingPulse phase={loadingPhase} />}
      <div ref={endRef} />
    </div>
  );
}
